//librerías
import { useCallback, useContext, useMemo, useState } from "react";
import {
  faClipboardCheck,
  faFilePdf,
  faMoneyBill,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ColumnDef } from "@tanstack/react-table";
import { Button, Container, ProgressBar, Spinner } from "react-bootstrap";
import { API } from "../../../global/global";
import { AuthContext } from "../../../context/AuthContext";

//componentes
import CobroModal from "../../Formularios/CobroModal/CobroModal";
import NoCobroModal from "../../Formularios/NoCobroModal/NoCobroModal";
import CustomTable from "../../Formularios/CustomTable/CustomTable";

//types
import { ServicioPorCobrar } from "../../../types/ServicioPorCobrar";
import { useGetData } from "../../../hooks/useGetData";
import { PDFPreviewModal } from "../../Common/PDFPreviewModal/PDFPreviewModal";
import { usePDFPreview } from "../../../hooks/usePDFPreview";

//estilos
const classes = require("./ServiciosTable.module.css").default;

/**
 * ServiciosPorCobrar Component
 * @description: Componente que muestra el paginado de los servicios que no se han cobrado.
 * @date 06/03/2023.
 * @param Props Ninguno
 * @returns JSX de la tabla de servicios por cobrar.
 */

const ServiciosPorCobrar = () => {
  const [actividadActual, setActividadActual] = useState<number>(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalNoCobro, setShowModalNoCobro] = useState<boolean>(false);
  const authContext = useContext(AuthContext);
  const { previewPDF, loading: loadingPDF, showModal: showPDFModal, pdfUrl, closeModal } = usePDFPreview();

  const { data: actividades, refetch } = useGetData(
    "/actividad?ultima_etapa[gte]=3&onlyCobroNull=true&includeAll=true",
    "actividadesPorCobrar"
  );

  //Definicion de las columnas de la tabla
  const columns = useMemo<ColumnDef<ServicioPorCobrar>[]>(
    () => [
      {
        header: "Órden de servicio", // nombre que aparecera en el encabezado de la tabla
        accessorKey: "id", //identificador de la columna
        cell: (info) => info.getValue(), // valor que aparecera en esa columna
        footer: (props) => props.column.id,
      },
      {
        header: "Fecha y hora de la cita",
        accessorKey: "fecha",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Técnico",
        accessorFn: (row) =>
          `${row.usuario.nombre} ${row.usuario.apellido_paterno} ${row.usuario.apellido_materno}`,
        id: "tecnico",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Tipo de servicio",
        accessorFn: (row) => row.tipo_servicio.nombre,
        id: "tipo_servicio",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Ticket",
        accessorFn: (row) => row.unidad.ticket,
        id: "ticket",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Placas",
        accessorFn: (row) => (row.unidad.placa ? row.unidad.placa : ""),
        id: "placa",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Descripción",
        accessorFn: (row) =>
          row.descripcion_servicio ? row.descripcion_servicio : "",
        id: "descripcion",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Acciones",
        cell: (info) => (
          <div className="btn-group" role="group" aria-label="Basic example">
            {/** Boton de despliegue del PDF */}
            <Button
              title="Obtener el PDF de este servicio"
              style={{ backgroundColor: "#5DADE2", borderColor: "white" }}
              disabled={loadingPDF}
              onClick={() => previewPDF(info.row.original.id, false)}
            >
              <FontAwesomeIcon icon={faFilePdf as IconProp} />
            </Button>
            {/** Boton de despliegue del modal Cobrar servicio*/}
            <Button
              title="Cobrar este servicio"
              style={{ backgroundColor: "#2874A6", borderColor: "white" }}
              onClick={() => {
                setShowModal(true);
                setActividadActual(info.row.original.id);
              }}
            >
              <FontAwesomeIcon icon={faMoneyBill as IconProp} />
            </Button>
            {/** Boton de despliegue del modal No cobrar servicio */}
            <Button
              title="NO cobrar"
              style={{ backgroundColor: "#21618C", borderColor: "white" }}
              onClick={() => {
                setShowModalNoCobro(true);
                setActividadActual(info.row.original.id);
              }}
            >
              <FontAwesomeIcon icon={faClipboardCheck as IconProp} />
            </Button>
          </div>
        ),
        footer: (props) => props.column.id,
      },
    ],
    [previewPDF, loadingPDF]
  );

  // Mientras se cargan las actividades se muestra un spinner
  if (!actividades) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner animation="grow" variant="info" />
      </div>
    );
  }

  return (
    <Container fluid style={{ paddingBottom: "1rem", paddingLeft: "2rem", paddingRight: "2rem" }}>
      <h1>Servicios por cobrar</h1>
      {loadingPDF && (
        <ProgressBar
          style={{ marginBottom: "1rem" }}
          label="Creando Archivo..."
          animated
          now={100}
        />
      )}
      {/** Tabla de actividades */}
      <CustomTable
        {...{
          data: actividades,
          columns,
        }}
      />
      {/** Modal de cobro */}
      {showModal && (
        <CobroModal
          actividadId={actividadActual}
          showModal={showModal}
          handleCloseModal={() => setShowModal(false)}
          refetch={refetch}
        />
      )}
      {/** Modal de no cobro */}
      {showModalNoCobro && (
        <NoCobroModal
          actividadId={actividadActual}
          showModal={showModalNoCobro}
          handleCloseModal={() => setShowModalNoCobro(false)}
          refetch={refetch}
        />
      )}
      
      {/** Modal de previsualización PDF */}
      <PDFPreviewModal
        show={showPDFModal}
        onHide={closeModal}
        pdfUrl={pdfUrl}
        title="Vista previa del servicio"
      />
    </Container>
  );
};

export default ServiciosPorCobrar;
